import React, { useEffect } from "react";
import { Box, Divider } from "@mui/material";
import { useFontTheme } from "../../../../shared/hooks/useFontTheme";
import { useAccentTheme } from "../../../../shared/hooks/useAccentTheme";
import { FONT_THEMES } from "../../../../shared/themes/fontThemes";
import { dt } from "../../../../shared/themes/redesignTokens";
import SettingsCard from "../shared/SettingsCard";
import useTranslation from "../../../../shared/hooks/useTranslation";
import customersApi from "../../../shared/api/customersApi";
import FontThemePreviewCard from "./FontThemePreviewCard";
import AccentColorPreviewCard from "./AccentColorPreviewCard";

// Preload Google Fonts for preview cards so sample text renders correctly
const PREVIEW_FONTS_URLS = Object.values(FONT_THEMES)
  .filter((t) => t.googleFontsUrl)
  .map((t) => t.googleFontsUrl!);

const SettingsAppearance: React.FC = () => {
  const { translate } = useTranslation("settings");
  const { themeId, setTheme, themes: fontThemes } = useFontTheme();
  const { accentId, setAccentTheme, themes: accentThemes } = useAccentTheme();

  // Preload all Google Fonts so previews render correctly
  useEffect(() => {
    PREVIEW_FONTS_URLS.forEach((url) => {
      const id = `font-preview-${btoa(url).slice(0, 12)}`;
      if (!document.getElementById(id)) {
        const link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        link.href = url;
        document.head.appendChild(link);
      }
    });
    // Also inject Geist Mono @font-face for preview
    const geistTheme = FONT_THEMES["space-grotesk-geist"];
    if (geistTheme?.selfHostedFontCss) {
      const id = "font-preview-geist-mono";
      if (!document.getElementById(id)) {
        const style = document.createElement("style");
        style.id = id;
        style.textContent = geistTheme.selfHostedFontCss;
        document.head.appendChild(style);
      }
    }
  }, []);

  const handleFontChange = (id: string) => {
    setTheme(id); // instant visual change + localStorage
    customersApi.updateFontTheme(id).catch(() => {
      // silent -- localStorage is the fallback
    });
  };

  const handleAccentChange = (id: string) => {
    setAccentTheme(id); // instant visual change + localStorage + CSS vars
    customersApi.updateAccentTheme(id).catch(() => {
      // silent -- localStorage is the fallback
    });
  };

  return (
    <SettingsCard
      title={translate("appearance.title")}
      subtitle={translate("appearance.description")}
    >
      {/* ── Typography Section ── */}
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "15px",
          fontWeight: 700,
          color: dt.textPrimary,
          mb: 0.5,
        }}
      >
        {translate("appearance.typographyTitle")}
      </Box>
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "13px",
          color: dt.textSecondary,
          mb: 2,
        }}
      >
        {translate("appearance.typographyDescription")}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        {fontThemes.map((theme) => (
          <FontThemePreviewCard
            key={theme.id}
            theme={theme}
            isActive={theme.id === themeId}
            onClick={() => handleFontChange(theme.id)}
            translate={translate}
          />
        ))}
      </Box>

      {/* ── Divider ── */}
      <Divider sx={{ my: 4, borderColor: "#E5E7EB" }} />

      {/* ── Accent Color Section ── */}
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "15px",
          fontWeight: 700,
          color: dt.textPrimary,
          mb: 0.5,
        }}
      >
        {translate("appearance.accentTitle")}
      </Box>
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "13px",
          color: dt.textSecondary,
          mb: 2,
        }}
      >
        {translate("appearance.accentDescription")}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        {accentThemes.map((preset) => (
          <AccentColorPreviewCard
            key={preset.id}
            preset={preset}
            isActive={preset.id === accentId}
            onClick={() => handleAccentChange(preset.id)}
            translate={translate}
          />
        ))}
      </Box>
    </SettingsCard>
  );
};

export default SettingsAppearance;
